/* eslint-disable import/no-cycle */
import React, { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import get from 'lodash/get';
import { WrappedFunctionProps } from '../types';
import { flattenUISchema } from '../helper';
import useDynaFormFactory from '../useDynaFormFactory';

/** Usage
 *  component: 'Array',
 *  scope: [
 *    'project'
 *  ],
 *  translation: {
 *    ...
 *    entryTitle: {
 *      de: 'Name Eintrags (e.g. Zahlungsmethode)',
 *      en: 'Name of the entry (e.g. payment method)',
 *    },
 *  },
 *  tabNameFrom: 'name',
 *  entry: {
 *    name: {
 *      component: 'TextField',
 *      scopes: [
 *        'project',
 *      ],
 *      translation: {
 *        title: {
 *          de: 'Name',
 *          en: 'Name',
 *        },
 *      },
 *    },
 *  },
 *
 * Make sure to set the scope as this component can not be automatically be hidden if the no children
 * are rendered for the current scope.
 *
 * FIXME this component may break on screens under the 600px breakpoint
 */

export default function Array({
  inputKey,
  control,
  data,
  language,
}: WrappedFunctionProps) {
  const { t } = useTranslation();
  const { toElements } = useDynaFormFactory();

  const [enablePanel, setEnablePanel] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  // "fields" is the array of values as it is kept in the field array state. The "tabs" variable
  // represents the array of values as it is kept in the form state. Only the former is instantly
  // updated when an entry is added or removed. So it is used for the lists (e.g. the list of tabs).
  // The latter is updated whenever an input field changes. So these values are needed to keep
  // atomic values at the most recent state. Therefore the list of tabs for example is rendered
  // from the "fields" array, but the text used for the tab list comes from the "tabs" array.
  const { fields, append, remove } = useFieldArray({ control, name: inputKey });
  const tabs = useWatch({ control, name: inputKey });

  // create an object of default values by mapping every key to an empty string
  // TODO extend memo do work with nested objects
  // TODO update memo to handle values other than strings
  const defaultValues = useMemo(
    () => Object.fromEntries(Object.keys(data.entry).map((d) => [d, ''])),
    [data.entry],
  );

  const handleAdd = useCallback(() => {
    append(defaultValues);
    // tabs array is not yet updated to no -1 is needed
    setTabIndex(fields.length);
  }, [append, defaultValues, fields.length]);

  const handleRemove = useCallback(
    (index: number) => {
      remove(index);
      // force full rerender of the panel because arrays and forms are out of sync after remove
      setEnablePanel(false);
      setTimeout(() => {
        setEnablePanel(true);
      }, 0);
      // tabs array is not yet updated so the -2 is needed
      if (tabIndex >= fields.length - 1) setTabIndex(fields.length - 2);
    },
    [fields.length, remove, tabIndex],
  );

  if (!data?.entry) return null;

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex' }}>
      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={tabIndex}
        onChange={(_: any, i: number) => {
          setTabIndex(i);
        }}
        sx={{ borderRight: 1, borderColor: 'divider', maxHeight: '500px' }}
      >
        {fields?.map((field: any, index: number) => (
          <Tab
            label={
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ width: '100%' }}
              >
                <Box
                  sx={{
                    // Wrapping tab text so that it does not break the layout
                    maxWidth: '300px',
                    minWidth: '50px',
                    width: 'fit-content',
                    maxHeight: '1em',
                    overflow: 'hidden',
                    paddingBlock: '2px',
                    textTransform: 'none',
                  }}
                >
                  {get(tabs?.[index] || {}, data.tabNameFrom) || '-/-'}
                </Box>
                <IconButton
                  color="error"
                  sx={{ padding: 0 }}
                  onClick={() => {
                    handleRemove(index);
                  }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Stack>
            }
            key={`array-component-tab-${field.id}`}
          />
        ))}
        <Button
          variant="outlined"
          endIcon={<AddIcon />}
          sx={{ marginInline: '16px' }}
          onClick={handleAdd}
        >
          {t('button.add')}
        </Button>
      </Tabs>

      {!!enablePanel &&
        fields.map((field: any, index: number) => {
          const path = `${inputKey}[${index}]`;
          return (
            <Box
              role="tabpanel"
              hidden={tabIndex !== index}
              sx={{ flexGrow: 1 }}
              key={`array-component-panel-${field.id}`}
            >
              {tabIndex === index && (
                <Stack gap={2} sx={{ padding: 3 }}>
                  {toElements(
                    {
                      elementDescriptions: flattenUISchema(data.entry),
                      mapping: data.mapping,
                    },
                    {
                      control,
                      language,
                      currentScope: data.currentScope,
                      namePrefix: path,
                    },
                  )}
                </Stack>
              )}
            </Box>
          );
        })}
    </Box>
  );
}
